import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { CustomizedTypography } from '@components/Customized';
import { RootState } from '@modules/index';
import { getMinDateFromToday } from '@utils/date';
import useQueryString from '@hooks/useQueryString';
import useRequest from '@hooks/useRequest';
import { RegisterRfqInput, RfqReferenceFileInput } from '@generated/graphql';
import {
  DetailedRequestInput,
  ProjectNameInput,
  ProjectPurposeSelect,
  DueDateInput,
  EstimateBudgetRangeInput
} from '../../FormComponents';
import { RequestQueryString } from '../../types';
import { DesignProcessSelectProps } from './DesignProcessSelect';
import DesignPrototypingControl from '@routes/NewRequest/FormComponents/DesignPrototypingControl';
import DesignReferenceFileInput from '@routes/NewRequest/FormComponents/DesignReferenceFileInput';
import ConsultMethodControl from '@routes/NewRequest/FormComponents/ConsultMethodControl';

const DesignRequestForm: React.FC<DesignProcessSelectProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const referenceFiles = useSelector<RootState, RfqReferenceFileInput[]>(
    (state) => state.request.referenceFiles ?? []
  );
  const requestPrototyping = useSelector<RootState, boolean | null>(
    (state) => state.request.requestPrototyping
  );
  const prototypeQuantity = useSelector<RootState, number | null>(
    (state) => state.request.prototypeQuantity
  );
  const [isValid, setIsValid] = useState<boolean>(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    query: { process, serviceType }
  } = useQueryString<RequestQueryString>();
  const { requestContainer } = usePCBRequestStyles();
  const { requestState, onUpdateRfqInfo } = useRequest();
  const processes = process?.split(',');
  const formProps = useForm<
    RegisterRfqInput & {
      requestPrototyping: boolean | null;
      prototypeQuantity: number | null;
    }
  >({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      ...requestState,
      dueDate:
        requestState?.dueDate !== ''
          ? requestState?.dueDate
          : getMinDateFromToday(7).toISOString(),
      requestPrototyping,
      prototypeQuantity: !requestPrototyping ? null : prototypeQuantity
    }
  });
  const { register, errors, watch } = formProps;
  const watchForm = watch();
  const currentFormValue = useRef<
    RegisterRfqInput & {
      requestPrototyping: boolean | null;
      prototypeQuantity: number | null;
    }
  >({
    ...requestState,
    dueDate:
      requestState?.dueDate !== ''
        ? requestState?.dueDate
        : getMinDateFromToday(7).toISOString(),
    prototypeQuantity,
    requestPrototyping
  });

  const validateForm = useCallback(() => {
    const {
      projectName,
      projectPurpose,
      dueDate,
      maxEstimatedBudget,
      detailedRequest,
      prototypeQuantity,
      requestPrototyping
    } = watchForm;

    if (referenceFiles.length === 0) {
      setIsValid(false);
      return;
    }

    if (requestPrototyping === null) {
      setIsValid(false);
      return;
    }

    if (requestPrototyping) {
      if (!prototypeQuantity) {
        setIsValid(false);
        return;
      }

      if (prototypeQuantity < 1) {
        setIsValid(false);
        return;
      }

      if (prototypeQuantity.toString().length >= 20) {
        setIsValid(false);
        return;
      }
    }

    if (
      projectName &&
      projectName.length <= 20 &&
      detailedRequest &&
      detailedRequest.length > 0 &&
      projectPurpose &&
      dueDate &&
      maxEstimatedBudget
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [watchForm, referenceFiles]);

  const onSubmit = useCallback(() => {
    onUpdateRfqInfo({
      ...currentFormValue.current
    });
  }, [onUpdateRfqInfo]);

  useEffect(() => {
    currentFormValue.current = {
      ...watchForm,
      drawingFiles: [],
      referenceFiles: requestState.referenceFiles
    };
  }, [watchForm, processes, requestState.referenceFiles]);

  useEffect(() => {
    nextButtonAction.current.DESIGN = onSubmit;
  });

  useEffect(() => {
    if (!setDisabledNextButton) return;

    setDisabledNextButton(!isValid);
  }, [isValid, setDisabledNextButton]);

  useEffect(() => {
    validateForm();
    //eslint-disable-next-line
  }, [watch, validateForm, referenceFiles]);

  useEffect(() => {
    return onSubmit;
    //eslint-disable-next-line
  }, []);

  return (
    <FormProvider {...formProps}>
      <form>
        <input
          type="hidden"
          ref={register({
            required: true
          })}
          name="serviceType"
          value={serviceType}
        />
        <div className={clsx([requestContainer, 'request-container'])}>
          <div className={clsx(['form-section'])}>
            <CustomizedTypography
              variant={!matches ? 'h2' : 'h3'}
              fontWeight="700"
              color="textPrimary"
              gutterBottoms={24}
            >
              참고할 파일을 첨부해주세요
            </CustomizedTypography>

            {/* 참고파일 */}
            <DesignReferenceFileInput />

            {/* 상세 요청사항 */}
            <DetailedRequestInput
              ref={register}
              error={errors.detailedRequest?.message}
            />
          </div>

          <div className={clsx(['form-section'])}>
            <CustomizedTypography
              variant={!matches ? 'h2' : 'h3'}
              fontWeight="700"
              color="textPrimary"
              gutterBottoms={24}
            >
              시제품 제작정보를 입력해주세요
            </CustomizedTypography>

            <DesignPrototypingControl
              error={errors.optionalServices?.message}
            />
          </div>

          <div className={clsx(['form-section'])}>
            <CustomizedTypography
              variant={!matches ? 'h2' : 'h3'}
              fontWeight="700"
              color="textPrimary"
              gutterBottoms={24}
            >
              프로젝트 정보를 입력해주세요
            </CustomizedTypography>

            <ProjectNameInput error={errors.projectName?.message} />
            <ProjectPurposeSelect error={errors.projectPurpose?.message} />
            <DueDateInput error={errors.dueDate?.message} />
            <EstimateBudgetRangeInput
              maxError={errors?.maxEstimatedBudget?.message}
            />
          </div>

          <div className={clsx(['form-section'])}>
            <CustomizedTypography
              variant={!matches ? 'h2' : 'h3'}
              fontWeight="700"
              color="textPrimary"
              gutterBottoms={24}
            >
              상담 시작 방식을 선택해주세요
            </CustomizedTypography>

            <ConsultMethodControl />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default DesignRequestForm;

const usePCBRequestStyles = makeStyles((theme) => ({
  requestContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '72px',
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0,
      paddingBottom: '32px'
    }
  }
}));
